import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface WeatherExTimeSlot {
  iso: string;
  index: number;
  label: string;
}

export interface VisibleTick {
  label: string;
  leftPercent: number;
}

@Component({
  selector: 'app-weatherex-forecast-timeline',
  templateUrl: './weatherex-forecast-timeline.component.html',
  styleUrls: ['./weatherex-forecast-timeline.component.scss'],
})
export class WeatherexForecastTimelineComponent {
  /** All available forecast dates (YYYY-MM-DD, sorted ascending). */
  @Input() dates: string[] = [];
  /** Currently selected date (YYYY-MM-DD). */
  @Input() selectedDate: string | null = null;
  /** Time slots for the selected date. */
  @Input() timeSlots: WeatherExTimeSlot[] = [];
  /** Slider position (0-based index into timeSlots) for the active time step. */
  @Input() sliderPosition = 0;
  /** Forecast update frequency / temporal resolution (e.g. '3h'). */
  @Input() updateFrequency: string | null = null;

  /** Emits the date string when the user picks a different day. */
  @Output() dateChange = new EventEmitter<string>();
  /** Emits the new slider position when the user moves the time slider. */
  @Output() timeChange = new EventEmitter<number>();
  /** Emits when the user closes the timeline. */
  @Output() closed = new EventEmitter<void>();

  /** At most 7 days are shown in the strip. */
  get visibleDates(): string[] {
    return this.dates.slice(0, 7);
  }

  /** Highest valid slider position. */
  get maxPosition(): number {
    return Math.max(0, this.timeSlots.length - 1);
  }

  /** Time label for the active slot (e.g. '05:00 UTC'). */
  get currentLabel(): string {
    const slot = this.timeSlots[this.sliderPosition];
    return slot ? slot.label : '';
  }

  /** Steps between labeled ticks, auto-adapted to data resolution.
   *  3h data  (≤8 steps)  → tick every 1 step  = every 3h
   *  1h data  (≤24 steps) → tick every 3 steps = every 3h
   *  15-min   (96 steps)  → tick every 4 steps = every 1h
   */
  private get tickInterval(): number {
    const n = this.timeSlots.length;
    if (n <= 8)  return 1;
    if (n <= 24) return 3;
    return 4;
  }

  /** Windowing is only needed for 15-min data (96 steps). */
  get useWindow(): boolean {
    return this.timeSlots.length > 24;
  }

  private get windowHalfSpan(): number {
    return this.useWindow ? 12 : this.maxPosition;
  }

  /** Tick marks visible in the current window with left-position percentages. */
  get visibleTicks(): VisibleTick[] {
    if (!this.timeSlots.length) return [];

    const half = this.windowHalfSpan;
    let winStart = Math.max(0, this.sliderPosition - half);
    let winEnd   = Math.min(this.maxPosition, this.sliderPosition + half);

    // Edge-clamp: keep window width constant at the extremes
    if (winEnd - winStart < 2 * half) {
      if (winStart === 0) {
        winEnd = Math.min(this.maxPosition, 2 * half);
      } else {
        winStart = Math.max(0, winEnd - 2 * half);
      }
    }

    const span = winEnd - winStart || 1;
    const ticks: VisibleTick[] = [];

    for (let i = 0; i <= this.maxPosition; i++) {
      if (i % this.tickInterval !== 0) continue;
      if (i < winStart || i > winEnd) continue;
      const slot = this.timeSlots[i];
      if (!slot) continue;
      ticks.push({
        label: slot.label.replace(/\s*UTC$/i, ''),
        leftPercent: ((i - winStart) / span) * 100,
      });
    }

    return ticks;
  }

  /** Thumb position as a percentage for the floating current-time label. */
  get thumbLeftPercent(): number {
    return this.maxPosition > 0 ? (this.sliderPosition / this.maxPosition) * 100 : 0;
  }

  /** Fill percentage for the CSS gradient. */
  get fillPercent(): number {
    return this.maxPosition > 0 ? (this.sliderPosition / this.maxPosition) * 100 : 0;
  }

  /** Track background gradient using CSS variables so dark-mode overrides work. */
  get trackBackground(): string {
    const pct = this.fillPercent;
    return `linear-gradient(to right, var(--wxt-fill-color) ${pct}%, var(--wxt-track-color) ${pct}%)`;
  }

  trackByLabel(_: number, tick: VisibleTick): string {
    return tick.label;
  }

  onSelectDate(date: string): void {
    if (!date || date === this.selectedDate) return;
    this.dateChange.emit(date);
  }

  onSliderChange(event: Event): void {
    this.timeChange.emit(+(event.target as HTMLInputElement).value);
  }

  onPrevStep(): void {
    if (this.sliderPosition > 0) {
      this.timeChange.emit(this.sliderPosition - 1);
    }
  }

  onNextStep(): void {
    if (this.sliderPosition < this.maxPosition) {
      this.timeChange.emit(this.sliderPosition + 1);
    }
  }

  onClose(): void {
    this.closed.emit();
  }
}
